import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ActivityRowsList } from "components/details-page-v-next/activity-rows";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { Spinner } from "components/ui/spinner";
import React, { useState } from "react";
import { pluralize } from "util/pluralize";

const initialDrillRows = 10;
const drillRowsIncrement = 25;

interface DrillRowsListProps<T> {
    resetKey: string;
    items: T[];
    keyOf: (item: T) => string;
    renderRow: (item: T) => React.ReactNode;
    capNote: string | null;
    testId: string;
}

export function DrillRowsList<T>({
    resetKey,
    items,
    keyOf,
    renderRow,
    capNote,
    testId,
}: DrillRowsListProps<T>): React.ReactElement {
    const [paging, setPaging] = useState({ resetKey, visibleCount: initialDrillRows });
    const visibleCount =
        paging.resetKey === resetKey ? paging.visibleCount : initialDrillRows;
    const hasMore = items.length > visibleCount;
    const showMore = () =>
        setPaging({ resetKey, visibleCount: visibleCount + drillRowsIncrement });
    return (
        <>
            <ActivityRowsList className="px-3 sm:px-4" testId={testId}>
                {items.slice(0, visibleCount).map(item => (
                    <li key={keyOf(item)}>{renderRow(item)}</li>
                ))}
            </ActivityRowsList>
            {hasMore ? (
                <div className="border-t border-gray-100 px-3 py-2 sm:px-4">
                    <InteractiveTextLink
                        className="inline-flex items-center gap-1 text-sm font-medium"
                        onClick={showMore}>
                        <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                        Show more
                    </InteractiveTextLink>
                </div>
            ) : capNote ? (
                <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 sm:px-4">
                    {capNote}
                </div>
            ) : null}
        </>
    );
}

interface DrillRecordsProps<T> {
    resetKey: string;
    noun: string;
    nounPlural: string;
    items: T[] | undefined;
    isLoading: boolean;
    isError: boolean;
    emptyMessage: string;
    atCap: boolean;
    capNote: string | null;
    keyOf: (item: T) => string;
    renderRow: (item: T) => React.ReactNode;
    onClear: () => void;
    testId: string;
}

export function DrillRecords<T>(props: DrillRecordsProps<T>): React.ReactElement {
    const { items, isLoading, isError } = props;
    return (
        <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-2.5 text-xs text-gray-500 sm:px-4">
                {items && !isError ? (
                    <span>
                        Showing{" "}
                        <span className="font-semibold text-gray-700">
                            {items.length}
                        </span>{" "}
                        {props.atCap ? "most recent " : ""}
                        {pluralize(items.length, props.noun, props.nounPlural)}
                    </span>
                ) : null}
                <InteractiveTextLink
                    className="text-xs font-medium"
                    onClick={props.onClear}>
                    Clear selection
                </InteractiveTextLink>
            </div>
            <div className="border-t border-gray-100">
                {isLoading ? (
                    <div className="flex justify-center px-4 py-8">
                        <Spinner />
                    </div>
                ) : isError ? (
                    <div className="px-4 py-8 text-center text-sm text-red-600">
                        We couldn&apos;t load these records. Please try again.
                    </div>
                ) : !items || items.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-600">
                        {props.emptyMessage}
                    </div>
                ) : (
                    <DrillRowsList
                        resetKey={props.resetKey}
                        items={items}
                        keyOf={props.keyOf}
                        renderRow={props.renderRow}
                        capNote={props.capNote}
                        testId={props.testId}
                    />
                )}
            </div>
        </>
    );
}
